import { Game } from "../../game.js";
import { AbstractOverlay } from "./abstract_overlay.js";
import { OverlayId } from "./overlay_ids.js";
import { OverlayManager } from "./overlay_manager.js";
import { LeaderboardInfoOverlay } from "./overlays/leaderboard_info_overlay.js";
import { GameResultOverlay } from "./overlays/game_result_overlay.js";
import { ContinueOverlay } from "./overlays/continue_overlay.js";
import { NewGoalOverlay } from "./overlays/new_goal_overlay.js";
import { ShopOverlay } from "./overlays/shop_overlay.js";
import { NewBoosterOverlay } from "./overlays/new_booster_overlay.js";
import { DailyTasksOverlay } from "./overlays/daily_tasks_overlay.js";
import { AdventureInfoOverlay } from "./overlays/adventure_info_overlay.js";
import { DailyChallengeInfoOverlay } from "./overlays/daily_challenge_info_overlay.js";
import { InfiniteLivesOverlay } from "./overlays/infinite_lives_overlay.js";
import { FeatureEndedOverlay } from "./overlays/feature_ended_overlay.js";
import { ImgID } from "../../image_loader/img_id.js";
import { FeatureTutorialOverlay } from "./overlays/feature_tutorial_overlay.js";
import { MysteryBoxOverlay } from "./overlays/mystery_box_overlay.js";
import { KeyHuntInfoOverlay } from "./overlays/key_hunt_info_overlay.js";
import { PrizeShotRewardsOverlay } from "./overlays/prize_shot_rewards_overlay.js";
import { TournamentWinOverlay } from "./overlays/tournament_win_overlay.js";
import { TournamentRewardOverlay } from "./overlays/tournament_reward_overlay.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { Screens } from "../../screen_manager/screen_manager.js";

export class OverlayFactory
{
  private static shop_preload_in_progress = false;

  public static openOverlay( overlayId: OverlayId, params?: any ): AbstractOverlay | null
  {
    // Unlike the regular shop screen, the shop overlay does not go through
    // ScreenManager.showScreenContent(), which preloads a screen's artwork
    // before constructing its UI. Constructing ShopScreen with missing assets
    // permanently bakes placeholders into its pattern/tile canvases, so wait
    // for the same preload here as well.
    if ( overlayId === OverlayId.SHOP_OVERLAY && !ImagesHolder.areScreensReady( [ Screens.SHOP ] ) )
    {
      if ( !this.shop_preload_in_progress )
      {
        this.shop_preload_in_progress = true;
        void ImagesHolder.preloadScreens( [ Screens.SHOP ], 10 )
          .then( () => this.createAndOpenOverlay( overlayId, params ) )
          .catch( e => console.warn( "OverlayFactory: shop image preload failed", e ) )
          .finally( () => { this.shop_preload_in_progress = false; } );
      }
      return null;
    }

    return this.createAndOpenOverlay( overlayId, params );
  }

  /** Overlays that go back to OverlayManager's pool, and the key their params build. */
  private static poolKeyFor( overlayId: OverlayId, params?: any ): string | null
  {
    switch ( overlayId )
    {
      case OverlayId.CONTINUE_OVERLAY:
        return ContinueOverlay.poolKeyFor( params );
      case OverlayId.PRIZE_SHOT_REWARDS_OVERLAY:
        return PrizeShotRewardsOverlay.poolKeyFor( params );
      default:
        return null;
    }
  }

  private static acquireOverlay( overlayId: OverlayId, params?: any ): AbstractOverlay | null
  {
    const key = OverlayManager.isDomMode() ? this.poolKeyFor( overlayId, params ) : null;
    const pooled = key ? OverlayManager.takePooled( key ) : null;
    if ( pooled )
    {
      pooled.resetForReuse();
      try
      {
        pooled.onReuse( params );
        return pooled;
      }
      catch ( e )
      {
        console.warn( `[OverlayFactory] onReuse failed for ${key}, rebuilding`, e );
        pooled.cleanup();
      }
    }
    return this.createOverlay( overlayId, params );
  }

  /**
   * Builds the overlays these params would open and leaves them warm in the
   * pool (OverlayManager.prewarm). The params must not act: their callbacks
   * are replaced by the real ones on open.
   */
  public static prewarmOverlays( requests: Array<{ id: OverlayId; params: any }> ): Promise<void>
  {
    if ( !OverlayManager.isDomMode() || OverlayManager.isAnyOverlayOpened() )
      return Promise.resolve();

    const overlays: AbstractOverlay[] = [];
    const keys = new Set<string>();
    for ( const { id, params } of requests )
    {
      const key = this.poolKeyFor( id, params );
      if ( !key || keys.has( key ) || OverlayManager.hasPooled( key ) )
        continue;
      keys.add( key );
      const overlay = this.createOverlay( id, params );
      if ( overlay )
        overlays.push( overlay );
    }
    return OverlayManager.prewarm( overlays );
  }

  private static createAndOpenOverlay( overlayId: OverlayId, params?: any ): AbstractOverlay | null
  {
    const overlay = this.acquireOverlay( overlayId, params );
    if ( overlay )
    {
      OverlayManager.openOverlay( overlay );
    } else
    {
      console.warn( `OverlayFactory: Unknown overlay ID: ${overlayId}` );
    }

    return overlay;
  }

  private static createOverlay( overlayId: OverlayId, params?: any ): AbstractOverlay | null
  {
    switch ( overlayId )
    {
      case OverlayId.NONE:
        return null;

      case OverlayId.LEADERBOARD_INFO_OVERLAY:
        return new LeaderboardInfoOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.GAME_RESULT_OVERLAY:
        return new GameResultOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.CONTINUE_OVERLAY:
        return new ContinueOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.NEW_GOAL_OVERLAY:
        return new NewGoalOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.NEW_BOOSTER_OVERLAY:
        return new NewBoosterOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.INFINITE_LIVES_OVERLAY:
        return new InfiniteLivesOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.SHOP_OVERLAY:
        return new ShopOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.KEY_HUNT_INFO_OVERLAY:
        return new KeyHuntInfoOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.PRIZE_SHOT_REWARDS_OVERLAY:
        return new PrizeShotRewardsOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.DAILY_TASKS_OVERLAY:
        return new DailyTasksOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.ADVENTURE_INFO_OVERLAY:
        return new AdventureInfoOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.ADVENTURE_ENDED_OVERLAY:
        return new FeatureEndedOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.DAILY_TASKS_ENDED_OVERLAY:
        return new FeatureEndedOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          {
            ...params,
            top_part_image_id: ImgID.UI_DailyTasksEndedTopPart,
            top_part_width: 780,
            top_part_height: 510,
            title_localization_key: "DAILY_TASKS.ENDED_OVERLAY.TITLE_TEXT",
            title_y: 492,
            title_width: 696,
            title_height: 215,
            title_font_size: 90,
          },
          overlayId
        );

      case OverlayId.DAILY_CHALLENGE_INFO_OVERLAY:
        return new DailyChallengeInfoOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.FEATURE_TUTORIAL_OVERLAY:
        return new FeatureTutorialOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.TOURNAMENT_WIN_OVERLAY:
        return new TournamentWinOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.TOURNAMENT_REWARD_OVERLAY:
        return new TournamentRewardOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      case OverlayId.MYSTERY_BOX_OVERLAY:
        return new MysteryBoxOverlay(
          OverlayManager.ctx,
          Game.safeAreaLeft(),
          Game.safeAreaTop(),
          Game.getContentWidth(),
          Game.getContentHeight(),
          params,
          overlayId
        );

      default:
        return null;
    }
  }

  public static isOverlayOpen( overlayId?: OverlayId ): boolean
  {
    return OverlayManager.isAnyOverlayOpened();
  }
}
